import React from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { BarChart3, DollarSign, TrendingUp, Zap } from 'lucide-react';
import { ChartCard, MetricCard, EmptyState, COLORS, CHART_COLORS, formatRevenue } from './common.jsx';

const OverviewTab = ({ data }) => {
    const {
        marketSize = {},
        publisherDistribution = [],
        topGenres = [],
        pricingBreakdown = [],
        publisherClassMetrics = []
    } = data || {};

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard
                    icon={<BarChart3 className="h-8 w-8" />}
                    title="Total Games"
                    value={marketSize.totalGames ? marketSize.totalGames.toLocaleString() : '—'}
                    subtitle="Analyzed titles"
                    color="blue"
                />
                <MetricCard
                    icon={<DollarSign className="h-8 w-8" />}
                    title="Total Revenue"
                    value={marketSize.totalRevenue ? formatRevenue(marketSize.totalRevenue) : '—'}
                    subtitle="Market size"
                    color="green"
                />
                <MetricCard
                    icon={<TrendingUp className="h-8 w-8" />}
                    title="Avg Price"
                    value={marketSize.averagePrice != null ? `$${marketSize.averagePrice.toFixed(2)}` : '—'}
                    subtitle="Per game"
                    color="purple"
                />
                <MetricCard
                    icon={<Zap className="h-8 w-8" />}
                    title="Revenue/Game"
                    value={marketSize.revenuePerGame ? formatRevenue(marketSize.revenuePerGame) : '—'}
                    subtitle="Average"
                    color="orange"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ChartCard title="Publisher Distribution" subtitle="Market share by publisher class">
                    {publisherDistribution && publisherDistribution.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={publisherDistribution}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={100}
                                    label={({ name, percentage }) => `${name} (${percentage}%)`}
                                >
                                    {publisherDistribution.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => value.toLocaleString()} />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <EmptyState message="No publisher distribution data available" />
                    )}
                </ChartCard>

                <ChartCard title="Top Genres" subtitle="By total revenue">
                    {topGenres && topGenres.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={topGenres.slice(0, 8)} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" tickFormatter={(value) => formatRevenue(value)} />
                                <YAxis dataKey="name" type="category" width={100} />
                                <Tooltip formatter={(value, name) => {
                                    if (name === 'Revenue' || name === 'Total Revenue') {
                                        return formatRevenue(value);
                                    }
                                    return value.toLocaleString();
                                }} />
                                <Bar dataKey="revenue" fill={COLORS.primary} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <EmptyState message="No genre data available" />
                    )}
                </ChartCard>

                <ChartCard title="Pricing Strategy" subtitle="Revenue by price range">
                    {pricingBreakdown && pricingBreakdown.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={pricingBreakdown}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="range" />
                                <YAxis yAxisId="left" orientation="left" />
                                <YAxis yAxisId="right" orientation="right" />
                                <Tooltip />
                                <Legend />
                                <Bar yAxisId="left" dataKey="gameCount" fill={COLORS.primary} name="Games" />
                                <Bar yAxisId="right" dataKey="revenue" fill={COLORS.success} name="Revenue" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <EmptyState message="No pricing data available" />
                    )}
                </ChartCard>

                <ChartCard title="Publisher Performance" subtitle="Revenue by class">
                    {publisherClassMetrics && publisherClassMetrics.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={publisherClassMetrics}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="class" />
                                <YAxis tickFormatter={(value) => formatRevenue(value)} />
                                <Tooltip formatter={(value, name) => {
                                    if (name === 'Revenue' || name === 'Total Revenue') {
                                        return formatRevenue(value);
                                    }
                                    return value.toLocaleString();
                                }} />
                                <Legend />
                                <Bar dataKey="totalRevenue" fill={COLORS.purple} name="Total Revenue" />
                                <Bar dataKey="avgRevenue" fill={COLORS.indigo} name="Avg Revenue" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <EmptyState message="No publisher metrics available" />
                    )}
                </ChartCard>
            </div>
        </div>
    );
};

export default OverviewTab;
